/**
 * 통합 관제 — 현장마다 따로 둔 서버들을 한 서버에서 모아 보고 원격 명령을 보내는 자리.
 *
 * 처음에는 공급사만 쓰던 화면이라 설명서에 없었다. 관리자가 서버를 묶고 명령을 보내게 되면서(2026-10-26) 장을 세웠다.
 * 토큰을 만드는 쪽(모이는 서버)은 환경 설정 장에 있고, 지도 보기는 모니터링 장에 있다 — 여기서는 그 둘로 보낸다.
 */
import type { ManualChapter } from '../types.js';

export const HUB: ManualChapter = {
  slug: 'hub',
  title: '통합 관제',
  summary: '현장마다 따로 둔 SignageOne 서버들을 한 서버에 묶어, 서버별 화면 현황을 모아 보고 그 서버의 화면에 재부팅·앱 종료·전원 종료를 보냅니다.',
  menuPath: '/hub',
  capability: 'feature:hub',
  sections: [
    {
      id: 'what',
      title: '무엇을 하는 화면인가',
      blocks: [
        {
          kind: 'text',
          text: '현장마다 서버를 따로 두면 콘솔도 서버 수만큼입니다. **통합 관제**는 한 서버(**모아 보는 서버**)가 다른 서버들(**모이는 서버**)의 화면 요약을 30초마다 모으고, 고른 서버의 화면 목록을 그 자리에서 받아 와 원격 명령을 보냅니다. 모이는 서버에서는 따로 켤 것이 없습니다 — 토큰 하나만 만들어 줍니다.',
        },
        {
          kind: 'table',
          header: ['통합 관제에서 하는 것', '각 서버의 콘솔에서 하는 것'],
          rows: [
            ['서버마다 화면 수·온라인·주의·정비 중, 그리고 그 합계', '콘텐츠·플레이리스트·스케줄·캠페인 편성'],
            ['고른 서버의 화면 목록 — 이름·위치·상태·버전·마지막 보고', '화면 등록, 이름·위치·그룹, 화면 설정'],
            ['재부팅·앱 종료·전원 종료·정비 모드 해제', '사용자 계정과 환경 설정'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '지도로 보려면',
          text: '원격 서버를 하나라도 등록하면 왼쪽 메뉴에 [[통합 모니터링]] 이 생겨, 이 서버와 원격 서버의 화면을 한 지도에 그립니다 — 통합 관제 메뉴가 없는 관리자에게도 보입니다. 원격 서버의 지역·사업장·주소는 통합 모니터링의 [[사업장 구성]] 에서 정합니다(통합 모니터링 장).',
        },
      ],
    },
    {
      id: 'connect',
      title: '서버 묶기',
      blocks: [
        {
          kind: 'text',
          text: '묶는 일은 두 서버의 관리자가 한 번씩 합니다 — 모이는 서버에서 토큰을 만들고, 모아 보는 서버에 그 토큰을 넣습니다. 둘 다 **환경 설정 권한**(관리자)이 필요합니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '모이는 서버의 [[환경 설정]] ▸ “통합 관제 연결” 에서 [[통합 관제 토큰 만들기]] 를 누르고 토큰(sk_…)을 복사합니다.', body: '**창을 떠나면 다시 볼 수 없습니다.** 패널 아래에 그 서버의 주소가 적혀 있습니다.' },
            { title: '모아 보는 서버의 [[통합 관제]] 에서 [[서버 추가]] 를 누릅니다.', body: '알아보기 쉬운 이름(예: 창원 공장), 모이는 서버의 주소, 1 에서 복사한 토큰을 넣습니다. 콘솔 주소를 통째로 붙여 넣어도 됩니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '저장하기 전에 그 서버에 닿는지·토큰이 통하는지 확인하고, 안 되면 까닭을 보여 줍니다(아래 자주 묻는 것). 되면 목록에 “연결됨” 으로 나오고 곧바로 화면 숫자가 채워집니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['두 서버의 망', '넣을 주소'],
          rows: [
            ['같은 사내망·VPN', '모이는 서버의 콘솔 주소(예: http://192.168.10.20:4600)'],
            ['망이 다르다', 'Sentinel Grid 주소(예: http://10.64.0.12:4600) — 두 서버에 그리드를 깐 공급사·설치 담당이 알려 줍니다'],
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '묶였는지 모이는 서버에서 보기',
          text: '모이는 서버의 [[환경 설정]] ▸ “통합 관제 연결” 의 토큰 줄에 **마지막 사용**이 적힙니다. 통합 관제가 30초마다 부르므로 몇 분 안쪽이면 묶여 있는 것입니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '통합 관제 메뉴가 없으면',
          text: '모아 보는 서버에 통합 관제 기능이 켜져 있어야 메뉴가 나옵니다 — 여러 서버용(Sentinel Grid) 판으로 설치한 서버는 켜진 채 설치됩니다. 메뉴가 없으면 공급사에 요청하세요. 화면 변경 권한이 없거나 담당 범위가 정해진 계정에는 보이지 않습니다.',
        },
      ],
    },
    {
      id: 'states',
      title: '연결 상태 읽기',
      blocks: [
        {
          kind: 'legend',
          title: '서버 줄의 “연결”',
          items: [
            { label: '연결됨', tone: 'ok', meaning: '30초마다 그 서버의 요약을 받고 있습니다.' },
            { label: '지연', tone: 'warn', meaning: '연결은 됐지만 마지막 요약이 1분 30초보다 오래됐습니다. 그 서버가 느리거나 모으기가 밀렸습니다.' },
            { label: '연결 끊김', tone: 'danger', meaning: '이 서버가 그 서버에 닿지 못합니다 — 망·그리드가 끊겼거나 그 서버가 꺼졌습니다. 줄 아래에 까닭이 한 줄 적힙니다.' },
            { label: '토큰 거절', tone: 'danger', meaning: '그 서버가 토큰을 받지 않습니다 — 그 서버에서 폐기했습니다. 이 서버를 백업에서 되살렸을 때도 이렇게 됩니다(백업에는 토큰이 담기지 않습니다).' },
            { label: '응답 오류', tone: 'warn', meaning: '닿기는 하지만 SignageOne 의 답이 아닙니다 — 주소·포트가 다른 서비스를 가리킵니다.' },
            { label: '확인 전', tone: 'neutral', meaning: '주소·토큰을 고친 뒤 아직 묻지 않았습니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '연결 끊김은 화면 장애가 아닙니다',
          text: '그 서버의 화면은 그 서버의 편성대로 계속 재생하고 있을 수 있습니다. 통합 관제는 마지막으로 받은 숫자를 **흐리게** 남기고, 합계에서는 빼고 “서버 · 연결 끊김 n” 으로 따로 셉니다. 먼저 그 서버의 콘솔이 열리는지 보세요 — 열리면 두 서버 사이의 길(사내망·Sentinel Grid)이 끊긴 것입니다.',
        },
      ],
    },
    {
      id: 'displays',
      title: '그 서버의 화면과 원격 명령',
      blocks: [
        {
          kind: 'text',
          text: '서버 줄을 누르면 아래에 **그 서버의 화면 목록**이 열립니다 — 그 서버에서 지금 받아 온 것이고, 다시 누르면 닫힙니다. 이름·위치로 찾고 전체·주의·온라인·오프라인으로 거릅니다. 200대가 넘으면 아래 [[더 보기]] 로 이어 받습니다. 원격 서버의 화면은 썸네일 없이 상태만 보여 줍니다 — 그 서버의 그림은 망이 달라 이 브라우저가 닿지 않을 수 있습니다.',
        },
        {
          kind: 'actions',
          title: '원격 명령',
          items: [
            { label: '기기 재부팅', text: '줄 끝의 둥근 화살표. 기기를 다시 켭니다 — 재부팅 권한이 있는 기기만(루트 또는 기기 소유자).' },
            { label: '앱 종료', text: '[⋯] 안. 재생 프로그램을 끕니다. 콘솔에서 다시 켤 수 없어 현장에서 다시 실행해야 합니다.' },
            { label: '전원 종료', text: '[⋯] 안. 기기 전원을 끕니다. 현장에서 전원을 넣어야 다시 켜집니다.' },
            { label: '정비 모드 해제', text: '정비 중인 화면에만 [⋯] 안에 나옵니다. 현장에서 열어 둔 채 떠난 정비 화면을 닫고 재생으로 돌아갑니다.' },
          ],
        },
        {
          kind: 'text',
          text: '누르면 확인 창이 한 번 뜹니다. **명령을 받아들일지는 그 서버가 정합니다** — 꺼진 화면, 명령을 모르는 옛 재생 프로그램, 권한이 없는 기기는 단추가 꺼져 있고 마우스를 올리면 까닭이 보입니다. 그 서버가 거절하면 「서버 이름」 과 그 서버의 말이 그대로 나옵니다. 조회 전용 토큰으로 묶은 서버는 명령 단추가 모두 꺼져 있습니다(서버 줄에 “조회 전용 토큰”). 보낸 명령은 이 서버와 그 서버 양쪽의 감사 기록에 남습니다.',
        },
      ],
    },
    {
      id: 'faq',
      title: '자주 묻는 것',
      blocks: [
        {
          kind: 'faq',
          items: [
            {
              q: '[[서버 추가]] 에서 “그 서버를 쓸 수 없습니다” 가 나옵니다.',
              a: '뒤에 붙은 까닭을 봅니다. “연결을 거부했습니다” 는 포트가 그 서버의 접속 포트가 아니고, “5초 안에 답이 없습니다”·“그 주소로 가는 길이 없습니다” 는 두 서버 사이의 망(그리드)이 닿지 않는 것입니다. “토큰을 거절했습니다” 면 토큰을 잘못 붙여 넣었거나 이미 폐기한 것이니 새로 만들어 넣습니다. “SignageOne 서버의 답이 아닙니다” 는 주소가 다른 서비스를 가리킵니다.',
            },
            {
              q: '잘 되던 서버가 “토큰 거절” 이 됐습니다.',
              a: '모이는 서버에서 그 토큰을 폐기했거나, 이 서버(모아 보는 서버)를 백업에서 되살렸을 때입니다 — 다른 서버를 여는 열쇠라 백업에 담지 않습니다. 모이는 서버의 [[환경 설정]] 에서 토큰을 새로 만들고, 여기서 서버 이름을 눌러 새 토큰을 넣고 저장합니다.',
            },
            {
              q: '서버를 지우면 그 서버의 화면은 어떻게 되나요?',
              a: '아무 일도 없습니다. 통합 관제에서 빠질 뿐이고, 그 서버와 화면은 그대로 돕니다. 다시 묶을 때 쓸 토큰이 없으면 모이는 서버에서 새로 만듭니다.',
            },
          ],
        },
      ],
    },
  ],
};
